import React, { useState, useEffect } from 'react';
import dbService from '../../../services/dbService';
import permissionService from '../../../services/permissionService';
import { usePermissions } from '../../../hooks/usePermissions';
import { toast } from 'react-toastify';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import PageContainer from '../../layout/PageContainer';

const permissionLabels = {
    canCreateUsers: 'Create Users', canEditUsers: 'Edit Users', canDeleteUsers: 'Delete Users',
    canEditRulesInSandbox: 'Edit Rules (Sandbox)', canDeployRules: 'Deploy Rules', canDeleteRules: 'Delete Rules',
    canAssignCases: 'Assign Cases', canManageMasters: 'Manage Masters Data',
};

const PermissionManagement = () => {
    const [permissionData, setPermissionData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const { permissions, isLoading: isPermissionsLoading } = usePermissions(); // CORRECT: De-structure the hook
    const { user } = useAuth();

    const fetchData = async () => {
        const data = await dbService.getAll('permissions');
        setPermissionData(data);
    };

    useEffect(() => { fetchData(); }, []);

    const handlePermissionChange = (role, permissionKey) => {
        setPermissionData(currentData =>
            currentData.map(roleData => {
                if (roleData.role === role) {
                    return {
                        ...roleData,
                        permissions: {
                            ...roleData.permissions,
                            [permissionKey]: !roleData.permissions[permissionKey],
                        },
                    };
                }
                return roleData;
            })
        );
    };

    const handleSaveChanges = async () => {
        setIsSaving(true);
        try {
            await Promise.all(
                permissionData.map(roleData => dbService.update('permissions', roleData))
            );
            permissionService.clearCache();
            await dbService.addLog({ user: user.name, action: 'Permissions Updated', details: 'User role permissions were modified.' });
            toast.success("Permissions updated successfully!");
        } catch (error) {
            toast.error("Failed to save permissions.");
        } finally {
            setIsSaving(false);
        }
    };

    // FIX: Wait for permissions to load before checking them
    if (isPermissionsLoading) {
        return <div className="text-center p-5"><Spinner animation="border" /></div>;
    }

    // FIX: Only check permissions *after* loading is complete
    if (!permissions.isAdmin) {
        toast.error("Access Denied: You must be an Admin to view this page.");
        return <Navigate to="/dashboard" />;
    }
    
    const roles = permissionData.map(p => p.role);
    const permissionKeys = Object.keys(permissionLabels);

    return (
        <PageContainer title='Permission Management'>
            <p>Dynamically control the capabilities of each user role. Changes will take effect upon the user's next page load or session.</p>
            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Permission</th>
                                    {roles.map(role => <th key={role} className="text-center">{role}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {permissionKeys.map(key => (
                                    <tr key={key}>
                                        <td>{permissionLabels[key]}</td>
                                        {roles.map(role => (
                                            <td key={`${role}-${key}`} className="text-center">
                                                <Form.Check 
                                                    type="switch"
                                                    id={`${role}-${key}`}
                                                    checked={permissionData.find(p => p.role === role)?.permissions[key] || false}
                                                    onChange={() => handlePermissionChange(role, key)}
                                                    disabled={role === 'L3 Admin'}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
                <div className="card-footer text-end">
                    <Button variant="primary" onClick={handleSaveChanges} disabled={isSaving}>
                        {isSaving ? <Spinner as="span" size="sm" /> : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </PageContainer>
    );
};

export default PermissionManagement;